import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Check, Compass, X, Zap } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Card, EmptyState } from "@/components/ranger/PageHeader";
import { StatusBadge } from "@/components/ranger/StatusBadge";
import { ConfigForm, CrudTable } from "@/components/ranger/Crud";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const DAYS = [[0, "Monday"], [1, "Tuesday"], [2, "Wednesday"], [3, "Thursday"], [4, "Friday"], [5, "Saturday"], [6, "Sunday"]];
const EVENT_FIELDS = [
  { key: "name", label: "Name", default: "" },
  { key: "kind", label: "Schedule", type: "select", options: [["once", "One time"], ["daily", "Daily"], ["weekly", "Weekly"], ["interval", "Every N minutes"]], default: "daily" },
  { key: "message", label: "Message (no quotes or semicolons)", type: "textarea", default: "" },
  { key: "run_at", label: "Date & time", type: "datetime-local", showIf: (v) => v.kind === "once" },
  { key: "time", label: "Time of day", type: "time", default: "18:00", showIf: (v) => ["daily", "weekly"].includes(v.kind) },
  { key: "weekday", label: "Day", type: "select", numeric: true, options: DAYS, default: 4, showIf: (v) => v.kind === "weekly" },
  { key: "interval_minutes", label: "Every (minutes)", type: "number", default: 60, showIf: (v) => v.kind === "interval" },
  { key: "timezone", label: "Timezone", default: "America/Chicago" },
  { key: "discord", label: "Post to Discord alert channel", type: "switch", default: true },
  { key: "ingame", label: "Broadcast in-game (say)", type: "switch", default: false, hint: "Goes out on the live server" },
  { key: "enabled", label: "Enabled", type: "switch", default: true },
];
const when = (_, r) => (r.kind === "once" ? `Once · ${r.run_at}` : r.kind === "interval" ? `Every ${r.interval_minutes}m` : r.kind === "daily" ? `Daily ${r.time}` : `${DAYS[r.weekday][1]} ${r.time}`);

export const Events = () => {
  const { active } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Phase 4 · Scheduler" title="Events" description="Scheduled announcements to Discord and in-game. The purge and wipe schedule lives in Settings → Purge schedule." />
      <CrudTable cid={active.id} path="events" title="Event" icon={Zap} fields={EVENT_FIELDS} emptyText="Schedule announcements such as raid-night reminders or server restarts."
        columns={[["name", "Name"], ["kind", "When", when], ["message", "Message"], ["last_run_at", "Last run", (v) => fmtTime(v)], ["last_result", "Result", (v) => (v ? Object.entries(v).map(([k, x]) => `${k}: ${x}`).join(" · ") : "—")], ["enabled", "On", (v) => (v ? "Yes" : "No")]]} />
    </div>
  );
};

export const Rules = () => {
  const { active } = useAuth();
  return (
    <div>
      <PageHeader eyebrow={active.mode === "pvp" ? "PvP server" : "PvE with monthly purge"} title="PvP / PvE Rules" description="Rules shown to players in the player portal. Rule enforcement tools live on the Moderation page." />
      <CrudTable cid={active.id} path="rules" title="Rule" icon={BookOpen} emptyText="Add the rules players must follow."
        fields={[{ key: "title", label: "Title", default: "" }, { key: "order", label: "Order", type: "number", default: 0 }, { key: "body", label: "Rule text", type: "textarea", default: "" }]}
        columns={[["order", "#"], ["title", "Title"], ["body", "Text"]]} />
    </div>
  );
};

const TP_STATE = { pending: "unconfigured", executed: "connected", denied: "error" };

export const Teleports = () => {
  const { active, can } = useAuth();
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get(`/communities/${active.id}/teleport-requests`).then((r) => setRows(r.data)).catch(() => {}), [active.id]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, a) => { try { await api.post(`/communities/${active.id}/teleport-requests/${id}/${a}`); toast.success("Done"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div>
      <PageHeader eyebrow="Phase 4" title="Teleports" description="Players request a teleport to another player from the portal; staff approve or deny." />
      <ConfigForm cid={active.id} field="teleports" title="Teleport settings" icon={Compass} fields={[
        { key: "enabled", label: "Teleports enabled", type: "switch", hint: "Uses 'teleport \"player\" \"target\"'. Unverified on RCE — test on staging before enabling." },
        { key: "cooldown_minutes", label: "Cooldown per player (minutes)", type: "number" }]} />
      <Card title="Requests">
        {rows.length === 0 ? <EmptyState title="No requests" testId="teleports-empty">Requests from the player portal appear here.</EmptyState> : (
          <Table data-testid="teleports-table">
            <TableHeader><TableRow className="border-[#1E2D4A]">{["Time", "Player", "Target", "Status", "Result", ""].map((h) => <TableHead key={h} className="rg-label">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.id} className="border-[#1E2D4A]/60 text-xs">
                <TableCell className="text-slate-400">{fmtTime(r.created_at)}</TableCell><TableCell className="text-slate-200">{r.gamertag}</TableCell><TableCell className="text-slate-200">{r.target}</TableCell>
                <TableCell><StatusBadge state={TP_STATE[r.status]} label={r.status} /></TableCell><TableCell className="text-slate-400">{r.result || "—"}</TableCell>
                <TableCell>{r.status === "pending" && can("moderator") && <div className="flex gap-2">
                  <button data-testid={`tp-approve-${r.id}`} aria-label="Approve" onClick={() => act(r.id, "approve")} className="text-emerald-400"><Check className="h-4 w-4" /></button>
                  <button data-testid={`tp-deny-${r.id}`} aria-label="Deny" onClick={() => act(r.id, "deny")} className="text-red-400"><X className="h-4 w-4" /></button>
                </div>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};
